import React from 'react';
import { 
  Send, 
  Paperclip, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  AlertCircle,
  Loader2,
  Smile,
  Mic,
  MoreVertical,
  Image
} from 'lucide-react';

const MessageInput = ({
  ticket,
  canReply,
  isResolvedOrClosed,
  newMessage,
  setNewMessage,
  selectedFiles,
  sendingMessage,
  uploadingFiles,
  onSendMessage,
  onSendFilesOnly,
  onUpdateStatus,
  actionLoading,
  fileInputRef,
  onFileSelect
}) => {
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (newMessage.trim() || selectedFiles.length > 0) {
        onSendMessage();
      }
    }
  };

  const getPlaceholder = () => {
    if (isResolvedOrClosed) {
      return "This ticket is closed. Reopen it to send messages...";
    }
    if (!canReply) {
      return "You cannot reply to this ticket in its current status...";
    }
    return "Type your message here...";
  };

  // Mobile-specific placeholder
  const getMobilePlaceholder = () => {
    if (isResolvedOrClosed) return "Ticket closed";
    if (!canReply) return "Cannot reply";
    return "Type message...";
  };

  return (
    <>
      {/* ========== DESKTOP VIEW ========== */}
      <div className="hidden lg:block bg-white rounded-xl shadow-lg p-4">
        {/* Status Alert for resolved/closed tickets */}
        {isResolvedOrClosed && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-yellow-800">
                    This ticket is {ticket.status.replace('_', ' ')}
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Reopen the ticket to continue the conversation with our support team.
                  </p>
                </div>
              </div>
              <button
                onClick={() => onUpdateStatus('open')}
                disabled={actionLoading}
                className="px-5 py-2.5 bg-[#ff660e] text-white rounded-lg font-medium hover:bg-[#e55a0c] transition disabled:opacity-50 flex items-center whitespace-nowrap"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Reopen Ticket
              </button>
            </div>
          </div>
        )}

        {/* Desktop Input Area */}
        <div className="flex items-start gap-3">
          {/* Attachment Button */}
          <div className="flex-shrink-0">
            <input
              type="file"
              ref={fileInputRef}
              onChange={onFileSelect}
              multiple
              className="hidden"
              disabled={!canReply || sendingMessage || uploadingFiles}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={!canReply || sendingMessage || uploadingFiles}
              className="p-3 border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              title="Attach files"
            >
              <Paperclip className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Text Input */}
          <div className="flex-1 relative">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={getPlaceholder()}
              disabled={!canReply || sendingMessage || uploadingFiles}
              rows={3}
              className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent resize-none transition ${
                !canReply || sendingMessage || uploadingFiles
                  ? 'bg-gray-100 cursor-not-allowed'
                  : 'bg-white hover:border-gray-400'
              }`}
            />
            
            {/* Character Counter & Helper Text */}
            <div className="absolute bottom-3 right-3 flex items-center gap-3">
              <span className="text-xs text-gray-500">
                Press <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">Enter</kbd> to send
              </span>
              <span className={`text-xs ${
                newMessage.length > 1800 ? 'text-red-500 font-medium' : 'text-gray-400'
              }`}>
                {newMessage.length}/2000
              </span>
            </div>

            {/* Additional Action Buttons (Desktop only) */}
            <div className="absolute top-3 right-3 flex items-center gap-2">
              <button
                type="button"
                className="p-1.5 hover:bg-gray-100 rounded-lg transition"
                title="Add emoji"
              >
                <Smile className="w-4 h-4 text-gray-500" />
              </button>
              <button
                type="button"
                className="p-1.5 hover:bg-gray-100 rounded-lg transition"
                title="Voice message"
              >
                <Mic className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Send Button & Quick Actions */}
          <div className="flex flex-col gap-2 flex-shrink-0">
            {/* Send Button */}
            <button
              onClick={onSendMessage}
              disabled={(!newMessage.trim() && selectedFiles.length === 0) || !canReply || sendingMessage || uploadingFiles}
              className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center justify-center min-w-[100px] ${
                (!newMessage.trim() && selectedFiles.length === 0) || !canReply || sendingMessage || uploadingFiles
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-[#ff660e] text-white hover:bg-[#e55a0c] shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
              }`}
            >
              {sendingMessage || uploadingFiles ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Send
                </>
              )}
            </button>
            
            {/* Quick Status Actions (Desktop) */}
            {canReply && !sendingMessage && !uploadingFiles && (
              <div className="flex gap-2">
                <button
                  onClick={() => onUpdateStatus('resolved')}
                  disabled={actionLoading}
                  className="flex-1 px-3 py-2 bg-green-50 border border-green-200 text-green-700 rounded-lg font-medium hover:bg-green-100 transition disabled:opacity-50 flex items-center justify-center text-sm"
                  title="Mark as resolved"
                >
                  <CheckCircle className="w-4 h-4 mr-1.5" />
                  Resolve
                </button>
                <button
                  onClick={() => onUpdateStatus('closed')}
                  disabled={actionLoading}
                  className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition disabled:opacity-50 flex items-center justify-center text-sm"
                  title="Close ticket"
                >
                  <XCircle className="w-4 h-4 mr-1.5" />
                  Close
                </button>
              </div>
            )}
          </div>
        </div>

        {/* File Info & Actions */}
        {selectedFiles.length > 0 && (
          <div className="mt-3 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected</span>
              {' '}
              {!newMessage.trim() && (
                <button
                  onClick={onSendFilesOnly}
                  disabled={sendingMessage || uploadingFiles}
                  className="text-[#ff660e] hover:text-[#e55a0c] font-medium hover:underline ml-2"
                >
                  Send files only
                </button>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-sm text-[#ff660e] hover:text-[#e55a0c] font-medium flex items-center"
            >
              <Image className="w-4 h-4 mr-1" />
              Add more
            </button>
          </div>
        )}

        {/* Helper Text */}
        <div className="mt-3 text-xs text-gray-500 flex items-center justify-between">
          <span>Maximum file size: 10MB • Supported: JPG, PNG, PDF, DOC, TXT</span>
          <span className="text-gray-400">Support typically responds within 24 hours</span>
        </div>
      </div>

      {/* ========== MOBILE VIEW ========== */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-3 px-4 safe-area-pb">
        {/* Mobile Status Alert */}
        {isResolvedOrClosed && (
          <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-yellow-800 text-sm">
                  Ticket is {ticket.status}
                </p>
                <button
                  onClick={() => onUpdateStatus('open')}
                  disabled={actionLoading}
                  className="mt-2 w-full px-4 py-2 bg-[#ff660e] text-white rounded-lg font-medium hover:bg-[#e55a0c] transition disabled:opacity-50 flex items-center justify-center text-sm"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Reopen to Reply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Input Area */}
        {canReply && !isResolvedOrClosed && (
          <>
            <div className="flex items-end gap-2">
              {/* Attachment Button */}
              <div className="flex-shrink-0">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={onFileSelect}
                  multiple
                  className="hidden"
                  disabled={sendingMessage || uploadingFiles}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sendingMessage || uploadingFiles}
                  className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl disabled:opacity-50 transition"
                >
                  <Paperclip className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Text Input */}
              <div className="flex-1 relative">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={getMobilePlaceholder()}
                  disabled={sendingMessage || uploadingFiles}
                  rows={2}
                  className={`w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent resize-none transition text-sm ${
                    sendingMessage || uploadingFiles
                      ? 'cursor-not-allowed'
                      : ''
                  }`}
                />
                {/* Character Counter Mobile */}
                {newMessage.length > 0 && (
                  <div className="absolute -top-6 right-0">
                    <span className={`text-xs ${
                      newMessage.length > 1800 ? 'text-red-500' : 'text-gray-500'
                    }`}>
                      {newMessage.length}/2000
                    </span>
                  </div>
                )}
              </div>

              {/* Send Button Mobile */}
              <button
                onClick={onSendMessage}
                disabled={(!newMessage.trim() && selectedFiles.length === 0) || sendingMessage || uploadingFiles}
                className={`p-3 rounded-xl flex-shrink-0 transition ${
                  (!newMessage.trim() && selectedFiles.length === 0) || sendingMessage || uploadingFiles
                    ? 'bg-gray-200 text-gray-500'
                    : 'bg-[#ff660e] text-white'
                }`}
              >
                {sendingMessage || uploadingFiles ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Mobile Quick Actions */}
            {selectedFiles.length === 0 && canReply && !sendingMessage && !uploadingFiles && (
              <div className="mt-3 flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    onClick={() => onUpdateStatus('resolved')}
                    disabled={actionLoading}
                    className="px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg font-medium text-sm hover:bg-green-100 transition disabled:opacity-50 flex items-center"
                  >
                    <CheckCircle className="w-4 h-4 mr-1.5" />
                    Resolve
                  </button>
                  <button
                    onClick={() => onUpdateStatus('closed')}
                    disabled={actionLoading}
                    className="px-3 py-1.5 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-200 transition disabled:opacity-50 flex items-center"
                  >
                    <XCircle className="w-4 h-4 mr-1.5" />
                    Close
                  </button>
                </div>
                
                <button className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Mobile File Info */}
            {selectedFiles.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} ready
                  </span>
                  {!newMessage.trim() && (
                    <button
                      onClick={onSendFilesOnly}
                      disabled={sendingMessage || uploadingFiles}
                      className="text-sm text-[#ff660e] font-medium"
                    >
                      Send files only
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-sm text-gray-600 flex items-center"
                  >
                    <Paperclip className="w-4 h-4 mr-1" />
                    Add more
                  </button>
                  <span className="text-xs text-gray-500">
                    Tap Send to attach
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Mobile disabled state when cannot reply */}
        {!canReply && !isResolvedOrClosed && (
          <div className="text-center py-3 px-4 bg-gray-100 rounded-lg">
            <p className="text-gray-600 text-sm font-medium">
              Cannot reply in current status
            </p>
            <p className="text-gray-500 text-xs mt-1">
              Ticket status: {ticket.status.replace('_', ' ')}
            </p>
          </div>
        )}

        {/* Mobile Safe Area Spacer */}
        <div className="h-4 lg:hidden"></div>
      </div>
    </>
  );
};

// Add CSS for safe area (for phones with notches)
const safeAreaStyles = `
  .safe-area-pb {
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0));
  }
`;

// Inject styles if not already present
if (typeof document !== 'undefined') {
  const styleId = 'safe-area-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = safeAreaStyles;
    document.head.appendChild(style);
  }
}

export default MessageInput;